import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HistoryIcon from '@mui/icons-material/History';
import { motion } from 'motion/react';
import {
  getIpamConfig, listIpamMembers, listIpamResourceCidrs,
} from '../../../../services/ipamApi';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState, PortalSectionHeader,
} from '../../../../components/portal/PortalStates';
import { pageVariants, sectionVariants } from '../../../../animations/variants';
import { formatStatusLabel } from '../../../../utils/statusLabel';
import {
  CK, FONT_BODY, FONT_MONO, UtilizationBar, bodyCellSx, cidrCellSx, headCellSx, rowSx,
  sectionCardSx, statusChipSx, tableHeadRowSx,
} from '../../portalStyles';
import IpamDiscoveryColdStart, { IPAM_DISCOVERY_POLL_MS } from './IpamDiscoveryColdStart';
import {
  IPAM_LIVENESS_UNREAD_BODY, IPAM_LIVENESS_UNREAD_TITLE,
  ipamAddressSpaceZeroState, ipamConfigNoteSeverity, ipamConfigNoteTitle,
  ipamResidualIpamCard, ipamScopeViewLagNotice,
} from './ipamCopy';
import IpamHistoryDrawer from './IpamHistoryDrawer';

// IpamAddressSpace — what IPAM DISCOVERED, joined to the scope's own posture.
//
// THIS PAGE AND IpamAllocations ARE NOT TWO VIEWS OF ONE DATASET, and a reader
// who assumes they are will misread both. This page is every CIDR AWS found on a
// real resource in the scope — a VPC exists, therefore its prefix is here,
// whether or not IPAM had anything to do with it. The Allocations page is the
// ledger of what IPAM HANDED OUT of its own pools. A VPC created outside a pool
// appears here and NOT there, and that delta is the entire compliance story: it
// is the address space nothing is governing. The banner below says so on screen,
// because saying it only in a design document is how the two pages get read as
// duplicates of each other.
//
// Reads GET /inventory/resource-cidrs, keyset-paginated. THAT ROUTE IS A MERGE,
// NOT A TABLE, and this page depends on it being one. ipam_scope_cidrs is the
// only source of the three posture columns, and AWS populates that per-scope view
// only AFTER the per-discovery view has landed — live-verified on the POC tenant
// 2026-08-23, where both scopes returned zero resource CIDRs while both
// discoveries returned seven each. Driving the list off the scope table alone
// made a freshly enabled tenant's 14 discovered CIDRs invisible and printed "AWS
// reported no CIDRs" over them. Every row now carries `scopeReported`, and every
// number and sentence on this screen branches on it.

const IPAM_COLOR = '#455A64';

const ALLOCATIONS_ROUTE = '/securityhub/ipam/allocations';
const ACCOUNTS_ROUTE = '/securityhub/ipam/accounts';
const IPAM_ROOT_ROUTE = '/securityhub/ipam';

// 350 ms, the portal's settled filter debounce (MacieBuckets.js:173).
const FILTER_DEBOUNCE_MS = 350;

// A refresh that fails three times running is a real fault, not a blip; below
// that a background poll stays quiet rather than throwing a red card over a
// screen that is rendering perfectly good data.
const MAX_POLL_FAILURES = 3;

// --- Is there an IPAM behind this config row? --------------------------------
//
// A CONFIG ROW IS NOT AN IPAM. One exists from the moment BeginIpamConfig runs —
// writeDraftConfig creates it at `awaiting_consent` — and a `failed` row persists
// forever afterwards. The sidebar renders all seven IPAM links whether or not the
// capability was ever enabled, so this page is directly reachable in every one of
// those states.
//
// `enabled` and `partial_failure` are the ONLY two statuses at which an IPAM
// exists and AWS's sweep is the thing being waited on. They are also exactly the
// pair StartRefresh accepts (services/ipam_service.go: "cannot refresh while VPC
// IPAM is %s"), which is what makes this the right predicate rather than a
// second, looser opinion about the same question.
const ipamIsLive = (status) => status === 'enabled' || status === 'partial_failure';

// The copy for a config row with NO live IPAM behind it and no sweep ever
// completed. Every one of these states used to render the cold-start panel —
// "AWS is still taking its first inventory… Nothing is wrong and there is nothing
// to retry" — under an indeterminate bar that will never finish, above a "Check
// for results" button whose refresh the backend refuses because the config is not
// settled. There is no IPAM, no discovery and no association: nothing will ever
// arrive, and each state has a different way out, so each one says its own.
const NOT_LIVE_META = {
  awaiting_consent: {
    title: 'VPC IPAM setup was never finished',
    description: 'Setup was started but stopped at the cost estimate, so no IPAM and no resource '
      + 'discovery were ever created. Nothing is being discovered and nothing is on its way — '
      + 'finish the setup wizard to start inventorying your address space.',
  },
  in_progress: {
    title: 'VPC IPAM is still being set up',
    description: 'Prism is still creating the IPAM and enrolling accounts, and AWS has not '
      + 'completed a discovery sweep yet. There is nothing to show here until it does.',
  },
  failed: {
    title: 'VPC IPAM setup failed',
    description: 'Setup did not finish, so there is no IPAM to discover anything. Nothing is '
      + 'running and nothing will resume on its own — re-run the setup wizard.',
  },
  blocked: {
    title: 'A VPC IPAM teardown is waiting on you',
    description: 'A teardown stopped part-way because member accounts still hold live '
      + 'allocations, and this IPAM never completed a discovery sweep, so there is no address '
      + 'space to show. The IPAM is still there and still billing until it is deleted.',
  },
  disabled: {
    title: 'VPC IPAM is not enabled yet',
    description: 'Nothing has been discovered because there is no IPAM to discover it. Enable '
      + 'VPC IPAM to start inventorying your address space.',
  },
};

const notLiveMeta = (status) => NOT_LIVE_META[status] || NOT_LIVE_META.disabled;

// Is there anything for this page to draw at all?
//
// `in_progress` and `blocked` are the two statuses that are not live and can
// still have a real IPAM behind them — an operation running over a working one,
// and a teardown parked on one that is still there and still billing — so those
// two are judged on whether a sweep has ever landed, and fall through to the
// table when it has.
//
// EVERY OTHER NON-LIVE STATUS IS JUDGED ON THE STATUS ALONE, deliberately
// ignoring firstDiscoveryAt. That timestamp "IS SET ONCE AND NEVER RESET"
// (services/ipam_service.go, writeRefreshFreshness) and BeginIpamConfig upserts
// the existing row, so a tenant who tore an IPAM down and started the wizard
// again sits at `awaiting_consent` still carrying the old one. Reading it as
// "this tenant has discovered before" would put stale rows under a live-looking
// table for a tenant with no IPAM at all.
const ipamHasNothingToShow = (config) => {
  if (!config) return true;
  if (ipamIsLive(config.status)) return false;
  if (config.status === 'in_progress' || config.status === 'blocked') {
    return !config.firstDiscoveryAt;
  }
  return true;
};

// THE THIRD ANSWER, PROPAGATED FROM IpamPools.js's ipamLiveness. A config row
// carrying NO STATUS AT ALL is not a decided negative — it is a field Prism never
// read — and without this arm NOT_LIVE_META's `disabled` fallback dresses the
// blank as "VPC IPAM is not enabled yet" and points an operator whose IPAM may be
// running and billing at the setup wizard. "Do not know" must not be spent as
// "not set up".
const ipamStatusUnread = (config) => !!config && !config.status;

// --- The one member filter ---------------------------------------------------
//
// SHARED WITH IpamDashboard.js, DELIBERATELY IDENTICAL. `disabled` is a
// tombstone and `removing` is a departure already in flight; neither is an
// account whose coverage could be missing, so neither belongs in any denominator
// on this page.
//
// It is load-bearing rather than tidy. ListMembers returns tombstones on purpose
// ("TOMBSTONES ARE RETURNED, NOT FILTERED OUT"), removeMember tombstones by
// writing {"phase", "error_message"} and nothing else, and syncIpamLifecycleStates
// skips `phase = disabled` cells — so an account removed BECAUSE its discovery was
// not-found keeps `resource_discovery_status = 'not-found'` forever. Without this
// filter the amber banner above names an account that is no longer enrolled, and
// goes on naming it permanently, while the Dashboard and Accounts pages correctly
// show nothing.
const isLiveMember = (m) => m?.phase !== 'disabled' && m?.phase !== 'removing';

// --- Response readers -------------------------------------------------------
//
// The keyset inventory routes now have ONE page envelope, fixed by
// services.IpamInventoryPage and asserted by the backend's own tests:
//
//   {"items": [...], "nextCursor": ""}
//
// `items` is NEVER null on a successful read — the un-enabled tenant and the
// cold-start tenant both answer `{"items": [], "nextCursor": ""}` — and
// `nextCursor` is always present, "" meaning the walk is exhausted.
//
// So this reader is deliberately INTOLERANT, and that is the change. It used to
// accept a bare array, or `{items}`, or `{rows}`, or a named key, which was the
// right call while the route answered 501 and nothing pinned the wrapper. It is
// the wrong call now: a reader that accepts four shapes cannot tell a renamed
// key from a normal response, so the day the envelope changes this table renders
// EMPTY — and an empty table here reads as an honest zero about a customer's
// address space. A broken envelope is raised as an error instead.
const ENVELOPE_ERROR = 'Prism could not read the discovered address space: the inventory service '
  + 'answered in a shape this page does not recognise. This is a defect, not an empty result — the '
  + 'table is deliberately not being drawn as "nothing found".';

const inventoryPage = (data) => {
  if (!data || typeof data !== 'object'
    || !Array.isArray(data.items) || typeof data.nextCursor !== 'string') {
    throw new Error(ENVELOPE_ERROR);
  }
  return data;
};

// scopeCidrTotal / discoveredCidrTotal are read as a THIRD VALUE and never
// coerced. They are absent from exactly one arm of the backend's own helper —
// ipamOkOrEmptyInventoryPage's not-found fallback writes {items, nextCursor} and
// nothing else — and `Number(undefined) || 0` there would manufacture the
// confident zero this whole change exists to delete. They are NOT part of the
// envelope check above for the same reason: a missing total is unknown, not a
// broken read.
const numberOrNull = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

// /members is NOT a keyset list and NOT that envelope: its handler
// (ipamOkOrEmptyPage) answers a bare array, and `[]` for a tenant with no config
// at all. Kept as its own reader so the two contracts cannot drift back into one
// tolerant helper that accepts either.
const bareList = (data) => (Array.isArray(data) ? data : []);

// --- Posture ----------------------------------------------------------------
//
// Each of the three posture columns has FOUR possible states, not two, and the
// two beyond AWS's own vocabulary are the ones that go wrong:
//
//   a real value    -> a chip
//   not_applicable  -> a muted em-dash with a tooltip, NEVER a chip. Prism stamps
//                      it at ingest on every non-VPC row (services/ipam_service.go
//                      :2884-2900) because AWS leaves the field blank there. An
//                      unknown state must never read as a clean one
//                      (OrphanScanPage.js:891-896).
//   blank on a vpc  -> a DIFFERENT muted state again. AWS does populate these on
//                      VPC rows, so a blank one is an anomaly worth seeing rather
//                      than a known-inapplicable value worth labelling — the
//                      service deliberately declines to launder it into
//                      not_applicable, and neither does this cell.
//   unrecognised    -> a WARNING chip carrying the raw token. A value nobody has
//                      mapped is not a healthy value.
const POSTURE_NOT_APPLICABLE = 'not_applicable';

const COMPLIANCE_META = {
  compliant: {
    intent: 'success',
    label: 'Compliant',
    hint: 'This CIDR sits inside space provisioned to an IPAM pool and matches that pool’s allocation rules.',
  },
  noncompliant: {
    intent: 'error',
    label: 'Noncompliant',
    hint: 'This CIDR was allocated from an IPAM pool but no longer matches its rules.',
  },
  unmanaged: {
    intent: 'warning',
    label: 'Unmanaged',
    hint: 'No IPAM pool governs this CIDR — the resource was given its addresses outside IPAM. It is real address space that nothing is planning around.',
  },
  ignored: {
    intent: 'neutral',
    label: 'Ignored',
    hint: 'This resource is excluded from IPAM monitoring, so IPAM makes no claim about it either way.',
  },
};

const OVERLAP_META = {
  nonoverlapping: {
    intent: 'success',
    label: 'No overlap',
    hint: 'No other CIDR in this scope collides with this one.',
  },
  overlapping: {
    intent: 'error',
    label: 'Overlapping',
    hint: 'Another CIDR in this scope collides with this one. Two resources — often in two different accounts — hold addresses that cannot both be routed.',
  },
  ignored: {
    intent: 'neutral',
    label: 'Ignored',
    hint: 'Excluded from overlap monitoring.',
  },
};

const MANAGEMENT_META = {
  managed: {
    intent: 'success',
    label: 'Managed',
    hint: 'IPAM is managing this resource’s CIDR.',
  },
  unmanaged: {
    intent: 'warning',
    label: 'Unmanaged',
    hint: 'IPAM knows about this resource but does not manage its CIDR.',
  },
  ignored: {
    intent: 'neutral',
    label: 'Ignored',
    hint: 'Excluded from IPAM management.',
  },
};

const NOT_APPLICABLE_HINT =
  'AWS reports compliance, overlap and management state on VPC rows only. This row is not a VPC, so there is no posture to report — that is not the same as the row being clean.';

const BLANK_ON_VPC_HINT =
  'AWS normally reports this on a VPC row and did not report it here. Treat it as unmeasured, not as a pass — it usually means the row was cached before AWS had finished assessing it.';

// The FOURTH state, and the one the live bug was hiding behind an empty table:
// the row is in ipam_discovered_cidrs and AWS's per-scope view has never
// mentioned it. There is no posture to render because nobody has been asked, not
// because AWS answered and left it blank.
const SCOPE_NOT_REPORTED_HINT =
  'AWS has not reported this CIDR in the IPAM scope view yet, so there is no compliance, overlap or management verdict for it. AWS populates that view after a discovery sweep lands, and it trails discovery on a newly enabled IPAM. This is unknown — not clean, and not a finding.';

function PostureCell({ value, meta, resourceType, scopeReported = true }) {
  const raw = String(value || '').trim();
  const isVpcRow = String(resourceType || '').toLowerCase() === 'vpc';

  // THE ROW THE SCOPE VIEW HAS NEVER SEEN. It takes the muted em-dash this page
  // already uses for "no posture on this row" rather than a fourth presentation
  // — the presentation is the established one, only the reason differs. On a
  // non-VPC row the older reason is the more informative of the two and is also
  // permanently true, so it wins: AWS will never report posture there whether or
  // not the scope view catches up.
  if (!scopeReported) {
    // The two branches are two different reasons and the spoken label says which
    // one — "Not yet reported" over the not-applicable hint would have a screen
    // reader announce a lag on a row whose posture is permanently absent.
    const hint = isVpcRow ? SCOPE_NOT_REPORTED_HINT : NOT_APPLICABLE_HINT;
    const spoken = isVpcRow ? 'Not yet reported' : 'Not applicable';
    return (
      <Tooltip title={hint}>
        <Box
          component="span"
          aria-label={`${spoken}. ${hint}`}
          sx={{ color: CK.muted, cursor: 'help' }}
        >
          &mdash;
        </Box>
      </Tooltip>
    );
  }

  if (raw === POSTURE_NOT_APPLICABLE) {
    return (
      <Tooltip title={NOT_APPLICABLE_HINT}>
        <Box
          component="span"
          aria-label={`Not applicable. ${NOT_APPLICABLE_HINT}`}
          sx={{ color: CK.muted, cursor: 'help' }}
        >
          &mdash;
        </Box>
      </Tooltip>
    );
  }

  if (!raw) {
    const hint = isVpcRow ? BLANK_ON_VPC_HINT : NOT_APPLICABLE_HINT;
    return (
      <Tooltip title={hint}>
        <Box
          component="span"
          aria-label={`Not reported. ${hint}`}
          sx={{ color: CK.muted, cursor: 'help', fontFamily: FONT_BODY, fontSize: '0.8rem' }}
        >
          not reported
        </Box>
      </Tooltip>
    );
  }

  const m = meta[raw.toLowerCase()];
  if (!m) {
    return (
      <Tooltip title={`Prism has no description for the value “${raw}”. It is shown as reported rather than folded into a state it may not be.`}>
        <Chip size="small" label={formatStatusLabel(raw)} sx={statusChipSx('warning')} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={m.hint}>
      <Chip size="small" label={m.label} sx={statusChipSx(m.intent)} />
    </Tooltip>
  );
}

// --- Utilization ------------------------------------------------------------
//
// AWS reports IpUsage as a DECIMAL FRACTION, not a percentage: the live spike saw
// `"IpUsage":0.375` on a 172.31.0.0/16 VPC (models/ipam_models.go:680), and the
// service copies the pointer through untouched. <UtilizationBar> takes 0..100, so
// the ×100 belongs here. Handing it 0.375 straight through would draw a 37 %-full
// VPC as "<1%" — the exact false all-clear about address exhaustion this whole
// capability exists to prevent.
//
// null SURVIVES AS null. AWS does not compute utilization for IPv6 space at all,
// so the field is absent forever on those rows; UtilizationBar renders that as a
// muted dash with UTILIZATION_UNAVAILABLE_TEXT rather than as 0 %.
const usagePercent = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n * 100;
};

// A subnet is a much smaller space than a VPC, so 90 % of a /28 is an outage where
// 90 % of a /16 is fine. This page mixes both and takes the TIGHTER bands, passed
// explicitly rather than inherited from UTILIZATION_THRESHOLDS.
const ADDRESS_SPACE_THRESHOLDS = { amber: 80, red: 95 };

// --- Misc helpers -----------------------------------------------------------

const parseRegions = (json) => {
  if (Array.isArray(json)) return json;
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v : [];
  } catch (_) {
    return [];
  }
};

const formatTime = (iso) => {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? null : t.toLocaleString();
};

// Options are derived from the rows ACTUALLY LOADED, so every option in a
// dropdown can match something. A value seeded from the URL is added even when no
// loaded row carries it — otherwise a deep link lands on a Select showing blank
// while silently filtering everything away.
const optionsFrom = (rows, key, current) => {
  const seen = new Set();
  rows.forEach((r) => {
    const v = r?.[key];
    if (v) seen.add(v);
  });
  if (current) seen.add(current);
  return [...seen].sort();
};

const plural = (n, one, many) => (n === 1 ? one : many);

export default function IpamAddressSpace() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [contextLoaded, setContextLoaded] = useState(false);

  // THE OUTCOME OF THE CONFIG READ, AS ITS OWN VALUE — 'pending' | 'ok' | 'failed'.
  //
  // `config === null` cannot carry it: it is the same null for "the read has not
  // returned", "the read rejected" and "the read returned the route's real answer
  // of no configuration". `error` cannot carry it either, because it was SHARED
  // with the inventory read — and that sharing is what produced the live defect.
  // loadContext() rejects, sets `error`, leaves `config` null; 350 ms later the
  // debounced load('') succeeds and calls setError(null); `noIpamYet` then reads
  // true and the page tells a customer whose IPAM is running and billing that it
  // is not enabled, taking the coverage banner down with it. Same tri-state as
  // IpamPublicIPs.js and IpamAccounts.js.
  const [configRead, setConfigRead] = useState('pending');

  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState('');
  // The two UNFILTERED tenant-wide totals the envelope carries. null is "the
  // reply did not say", which is neither zero nor an error — see numberOrNull.
  const [scopeTotal, setScopeTotal] = useState(null);
  const [discoveredTotal, setDiscoveredTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // `error` belongs to the CONTEXT read and to nothing else. The CIDR feed keeps
  // its own `tableError`, so a successful inventory page can no longer clear an
  // error the config read owns — which is exactly how the not-enabled falsehood
  // got onto the screen.
  const [error, setError] = useState(null);
  const [tableError, setTableError] = useState(null);
  const [checking, setChecking] = useState(false);

  // Filters, seeded from the URL so the dashboard's posture tiles can deep-link
  // straight to "the overlapping CIDRs in account X".
  const [account, setAccount] = useState(searchParams.get('account') || '');
  const [region, setRegion] = useState(searchParams.get('region') || '');
  const [resourceType, setResourceType] = useState(searchParams.get('type') || '');
  const [compliance, setCompliance] = useState(searchParams.get('compliance') || '');
  const [overlap, setOverlap] = useState(searchParams.get('overlap') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const [historyRow, setHistoryRow] = useState(null);

  // MONOTONIC REQUEST GUARD — NOT OPTIONAL, and not made redundant by the
  // debounce. The debounce suppresses keystrokes; it does not order responses.
  // Every filter change resets the keyset walk to page one, so a fast typist has
  // several first-page requests in flight at once, and without this guard a slow
  // early one resolves last and repaints the table with results for a query the
  // user has already changed. Same guard as MacieBuckets.js:149 and FindingsList.
  const reqSeq = useRef(0);
  const pollFailuresRef = useRef(0);

  const loadContext = useCallback(async ({ quiet = false } = {}) => {
    try {
      const [cfgRes, memberRes] = await Promise.all([getIpamConfig(), listIpamMembers()]);
      setConfig(cfgRes?.data ?? null);
      setMembers(bareList(memberRes?.data));
      pollFailuresRef.current = 0;
      setConfigRead('ok');
      if (!quiet) setError(null);
      return true;
    } catch (e) {
      pollFailuresRef.current += 1;
      // Conservative on purpose: the two reads share one try, so a members
      // failure also marks the config unread. Erring towards "we do not know"
      // costs a retry the operator can press; erring the other way is a claim
      // about a customer's setup made out of a rejected request.
      //
      // The FLAG is set on every rejection, quiet or not. `error` is deliberately
      // suppressed for the first two quiet poll failures so a transient 502 does
      // not flash a red card over a working page — but "quiet" must never mean
      // "and therefore the config read succeeded".
      setConfigRead('failed');
      if (!quiet || pollFailuresRef.current >= MAX_POLL_FAILURES) {
        setError(e?.message || 'Could not read the VPC IPAM configuration.');
      }
      return false;
    } finally {
      setContextLoaded(true);
    }
  }, []);

  // `limit` is deliberately NOT passed. The clamp is server-side and delegates to
  // services.shClampFindingsLimit; a second spelling of it in a page is how the
  // 50-row findings ceiling shipped in the first place.
  const load = useCallback(async (from) => {
    const myReq = ++reqSeq.current;
    if (from) setLoadingMore(true);
    else setLoading(true);
    try {
      const res = await listIpamResourceCidrs(from ? { cursor: from } : {});
      if (myReq !== reqSeq.current) return;
      const page = inventoryPage(res?.data);
      setRows((prev) => (from ? [...prev, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
      setScopeTotal(numberOrNull(page.scopeCidrTotal));
      setDiscoveredTotal(numberOrNull(page.discoveredCidrTotal));
      setTableError(null);
    } catch (e) {
      if (myReq !== reqSeq.current) return;
      setTableError(e?.message || 'Could not read the discovered address space.');
    } finally {
      if (myReq === reqSeq.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  // Debounced filter -> URL sync -> restart the keyset walk at page one.
  //
  // THE REFETCH IS NOT WASTE, even though the request carries no filter keys
  // today: a filter change invalidates the accumulated pages, because filtering
  // over an arbitrary prefix of the walk would answer "does anything match?" with
  // "nothing on the pages you happened to have loaded". Restarting the walk is
  // also what makes the reqSeq guard load-bearing. When the backend grows a filter
  // struct the keys join the call here and nothing else on the page changes.
  useEffect(() => {
    setRows([]);
    setNextCursor('');
    const t = setTimeout(() => {
      const params = {};
      Object.entries({
        account, region, type: resourceType, compliance, overlap, search,
      }).forEach(([k, v]) => { if (v) params[k] = v; });
      setSearchParams(params, { replace: true });
      load('');
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [account, region, resourceType, compliance, overlap, search, load, setSearchParams]);

  // --- The three empty states -----------------------------------------------
  //
  // Cold start is discriminated by the SERVER's config.firstDiscoveryAt being
  // null, never by a client-side timer: an elapsed-time heuristic is wrong across
  // a reload, wrong for a tab left open overnight, and wrong for an account added
  // six months later (DD-30).
  //
  // AND BY THE STATUS AS WELL AS THE TIMESTAMP. `firstDiscoveryAt == null` on its
  // own means "no sweep has ever landed", which is true both of a tenant waiting
  // on AWS and of a tenant that never built an IPAM at all. Only the first of
  // those is a cold start; the second gets the card below, which names the state
  // it is actually in.
  //
  // The two are mutually exclusive and leave no gap: cold start requires a LIVE
  // config, which is the first thing ipamHasNothingToShow rules out.
  //
  // AND `noIpamYet` IS GATED ON THE READ OUTCOME, not merely on `error`. The old
  // predicate held only while every rejection path happens to leave a truthy
  // message in a field the inventory read also writes; the branch that inherited
  // the case where one does not is a claim about the customer's setup.
  const configUnread = contextLoaded && (error || configRead === 'failed') && !config;
  const noIpamYet = contextLoaded && configRead === 'ok' && !error
    && ipamHasNothingToShow(config) && !ipamStatusUnread(config);
  // The blank-status arm, kept apart from `noIpamYet` so the two never share a
  // card: one is a state Prism read, the other is a read that produced nothing.
  const livenessUnread = contextLoaded && configRead === 'ok' && !error && ipamStatusUnread(config);
  // FIX: an IPAM can outlive the config row's status. See ipamResidualIpamCard —
  // a clean teardown of an ADOPTED IPAM settles `disabled` over one that is never
  // deleted and keeps metering, and `failed` can sit over an IPAM CreateIpam
  // successfully created. Null whenever there is no such evidence.
  const residualCard = ipamResidualIpamCard(config, 'discovered address space');
  const noteSeverity = ipamConfigNoteSeverity(config?.note, config?.noteSource);
  const noteTitle = ipamConfigNoteTitle(config?.note, config?.noteSource);
  const coldStart = !!config && ipamIsLive(config.status) && !config.firstDiscoveryAt;

  const liveMembers = useMemo(() => members.filter(isLiveMember), [members]);

  // Silent breakage: AWS reports `not-found` when a member deleted its resource
  // discovery or its RAM share was revoked. The association still exists and still
  // consumes quota while NOTHING is being discovered, and no other field in the
  // row changes — so this is the only signal that the tables below are missing
  // whole accounts however clean they look.
  const broken = useMemo(
    () => liveMembers.filter((m) => m?.resourceDiscoveryStatus === 'not-found'),
    [liveMembers],
  );
  const discovering = useMemo(
    () => liveMembers.filter((m) => m?.resourceDiscoveryStatus !== 'not-found'),
    [liveMembers],
  );

  const associations = useMemo(
    () => liveMembers
      .filter((m) => m?.resourceDiscoveryStatus)
      .map((m) => ({
        accountId: m.accountId,
        region: m.discoveryRegion || config?.homeRegion || '',
        status: m.resourceDiscoveryStatus,
      })),
    [liveMembers, config],
  );

  const regionCount = parseRegions(config?.operatingRegions).length;

  const checkNow = useCallback(async () => {
    setChecking(true);
    try {
      await loadContext({ quiet: true });
      await load('');
    } finally {
      setChecking(false);
    }
  }, [loadContext, load]);

  // 30 s, not the shared 3 s POLL_INTERVAL_MS: the first AWS discovery sweep takes
  // up to ~20 minutes, and a 3 s cadence is ~400 pointless requests per tenant per
  // cold start. Keyed on the PRIMITIVE `coldStart`, never on `config`, so a
  // progress tick cannot rebuild the interval and drift the cadence.
  useEffect(() => {
    if (!coldStart) return undefined;
    const id = setInterval(() => {
      if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
        clearInterval(id);
        return;
      }
      checkNow();
    }, IPAM_DISCOVERY_POLL_MS);
    return () => clearInterval(id);
  }, [coldStart, checkNow]);

  // --- Client-side filtering --------------------------------------------------
  //
  // Server-side filter keys beyond cursor/limit DO NOT EXIST: there is no filter
  // struct behind /inventory/resource-cidrs, and a query key the backend ignores
  // is a filter that silently does nothing. So the filtering happens here, over
  // the rows fetched so far, and the caption under the table says exactly that
  // whenever more pages remain — a filter that quietly searched one page of many
  // while looking like it searched everything is the same lie in a nicer suit.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (account && r?.resourceOwnerId !== account) return false;
      if (region && r?.resourceRegion !== region) return false;
      if (resourceType && r?.resourceType !== resourceType) return false;
      if (compliance && r?.complianceStatus !== compliance) return false;
      if (overlap && r?.overlapStatus !== overlap) return false;
      if (!q) return true;
      return [r?.resourceCidr, r?.resourceId, r?.resourceName, r?.vpcId, r?.ipamPoolId]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, account, region, resourceType, compliance, overlap, search]);

  const accountOptions = useMemo(() => optionsFrom(rows, 'resourceOwnerId', account), [rows, account]);
  const regionOptions = useMemo(() => optionsFrom(rows, 'resourceRegion', region), [rows, region]);
  const typeOptions = useMemo(() => optionsFrom(rows, 'resourceType', resourceType), [rows, resourceType]);
  const complianceOptions = useMemo(
    () => optionsFrom(rows, 'complianceStatus', compliance), [rows, compliance],
  );
  const overlapOptions = useMemo(() => optionsFrom(rows, 'overlapStatus', overlap), [rows, overlap]);

  // Any posture number on screen is VPC-SCOPED and the page says so — the three
  // columns are blank on every other row type, so a count that folded subnets in
  // would be a count over rows the number cannot describe.
  //
  // AND IT IS SCOPE-REPORTED-SCOPED TOO, which is the half the live bug exposed.
  // "0 overlapping of 2 VPCs" over two VPCs AWS has never assessed is the same
  // false all-clear as the empty table was, in a smaller font: nothing has looked
  // at those two, so nothing can say they do not collide. They are counted
  // separately and named on screen, never folded into the denominator of a clean
  // verdict.
  const vpcRows = useMemo(
    () => filtered.filter((r) => String(r?.resourceType || '').toLowerCase() === 'vpc'),
    [filtered],
  );
  // `=== true`, NOT `!== false`. The Go bool always serializes, so the absent case
  // is unreachable today — but the fallback direction is the whole subject of this
  // change, and defaulting a missing flag to "assessed" is the confident-negative
  // reflex in miniature. Absent reads as unknown on both sides of every count here.
  const assessedVpcCount = useMemo(
    () => vpcRows.filter((r) => r?.scopeReported === true).length,
    [vpcRows],
  );
  const unassessedVpcCount = vpcRows.length - assessedVpcCount;
  const overlappingCount = useMemo(
    () => filtered.filter((r) => r?.overlapStatus === 'overlapping').length,
    [filtered],
  );

  // The rows the scope view has and has not reached. Counted over `rows`, not
  // `filtered`: the notice explains a property of what has been LOADED, and a
  // caveat that appeared and vanished as the user typed in the filter box would
  // read as a glitch rather than as a fact about the data.
  const unreportedCount = useMemo(
    () => rows.filter((r) => r?.scopeReported !== true).length,
    [rows],
  );
  const reportedCount = rows.length - unreportedCount;
  const lagNotice = useMemo(
    () => ipamScopeViewLagNotice({ unreported: unreportedCount, reported: reportedCount, scopeTotal }),
    [unreportedCount, reportedCount, scopeTotal],
  );

  const filtersActive = !!(account || region || resourceType || compliance || overlap || search);

  const lastRefreshed = formatTime(config?.lastDiscoveryRefreshAt);

  // THE UNFILTERED ZERO, AND IT IS NOT ONE SENTENCE. "AWS reported no CIDRs" is
  // a claim, and on the POC tenant on 2026-08-23 this page spent it, with a
  // timestamp, over 14 CIDRs AWS had reported and Prism had stored. The four
  // arms live in ipamCopy.js so the same claim cannot be re-typed differently on
  // the sibling screens that make it; see ipamAddressSpaceZeroState.
  //
  // AND THE TWO TOTALS ARE NOT THE WHOLE DISCRIMINATOR. Both are counts over
  // PRISM'S OWN tables, so a zero is equally "AWS reported nothing" and "the read
  // never landed, so nothing was ever written". The server's per-cache freshness
  // stamps separate those, and until this change NOBODY READ THEM: three of the
  // five (scope, discovered-CIDR, discovered-account) were written every refresh
  // and consumed nowhere.
  const zeroState = useMemo(() => ipamAddressSpaceZeroState({
    discoveredTotal,
    scopeTotal,
    scopeCidrsReadAt: config?.scopeCidrsReadAt,
    discoveredCidrsReadAt: config?.discoveredCidrsReadAt,
    accountCount: discovering.length,
    regionCount,
    someAccountsBroken: broken.length > 0,
    lastRefreshed,
    morePagesRemain: !!nextCursor,
  }), [
    discoveredTotal, scopeTotal, config?.scopeCidrsReadAt, config?.discoveredCidrsReadAt,
    discovering.length, regionCount, broken.length, lastRefreshed, nextCursor,
  ]);

  const openHistory = (row) => setHistoryRow(row);

  const header = (
    <PortalPageHeader
      icon={<LanOutlinedIcon sx={{ fontSize: 24 }} />}
      title="Address space"
      subtitle="Every CIDR AWS discovered on a real resource, with the scope’s compliance and overlap verdict"
      color={IPAM_COLOR}
    />
  );

  // MUST PRECEDE EVERYTHING ELSE. A rejection from GET /config leaves `config`
  // null, and without this the page falls through to a branch that states what
  // the customer's setup IS — while the one read that would have established it
  // is the read that failed. Same guard as IpamPublicIPs.js and IpamAccounts.js.
  if (configUnread) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState
            error={error || 'Prism could not read this customer’s VPC IPAM configuration.'}
            onRetry={() => { loadContext(); load(''); }}
          />
        </motion.div>
      </motion.div>
    );
  }

  // ── THE NOT-LIVE GATE, ABOVE THE COVERAGE BANNER RATHER THAN BESIDE IT ─────
  //
  // This used to sit BELOW the "N of M accounts are no longer being discovered"
  // warning, so a present-tense coverage claim about AWS stacked on top of "VPC
  // IPAM is not enabled yet" — two sentences about the same tenant that cannot
  // both be true. IpamPublicIPs.js has always returned early here; this now
  // matches it.
  if (noIpamYet || livenessUnread) {
    const meta = livenessUnread
      ? { title: IPAM_LIVENESS_UNREAD_TITLE, description: IPAM_LIVENESS_UNREAD_BODY }
      : (residualCard || notLiveMeta(config?.status));
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<LanOutlinedIcon sx={{ fontSize: 28 }} />}
              title={meta.title}
              description={meta.description}
              color={IPAM_COLOR}
              action={(
                <Button
                  variant="contained"
                  component={RouterLink}
                  to={IPAM_ROOT_ROUTE}
                  sx={{ fontFamily: FONT_BODY, textTransform: 'none', backgroundColor: IPAM_COLOR }}
                >
                  Go to VPC IPAM
                </Button>
              )}
            />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{header}</motion.div>

      {/* THE DEGRADATION NOTE, ON THE PAGE THE STALE ROWS ARE ON. `config.note`
          carries the unattributed degradation channel — "AWS no longer reports
          IPAM %s … Nothing is being discovered" — which rendered as
          severity="info" on the dashboard and on no other page. */}
      {noteSeverity && (
        <motion.div variants={sectionVariants}>
          <Alert severity={noteSeverity} sx={{ mb: 3, fontFamily: FONT_BODY }}>
            {noteTitle && (
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>{noteTitle}</AlertTitle>
            )}
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>{config.note}</Typography>
          </Alert>
        </motion.div>
      )}

      {/* THE COVERAGE BANNER, ABOVE EVERYTHING. A gap in what was looked at is
          always drawn as a warning above the results, never as a footnote beneath
          them (OrphanScanPage.js:573-600) — the whole point is that the table
          below it looks perfectly healthy. */}
      {broken.length > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, fontFamily: FONT_BODY }}>
            {/* The denominator is the LIVE members, never `members.length`: a
                removed account is not a coverage gap, and counting its tombstone
                here made this sentence disagree with the Accounts page's own
                enrolled count on the same data. */}
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {broken.length} of {liveMembers.length}{' '}
              {plural(liveMembers.length, 'account is', 'accounts are')} no longer being discovered.
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', mb: 1 }}>
              Their resource discovery was deleted or their RAM share was revoked, so anything
              in {plural(broken.length, 'that account', 'those accounts')} is missing from every
              table on this page, however clean it looks.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              {broken.map((m) => (
                <Chip
                  key={m.accountId}
                  size="small"
                  label={`${m.accountId}${m.discoveryRegion ? ` · ${m.discoveryRegion}` : ''}`}
                  sx={statusChipSx('error')}
                />
              ))}
            </Stack>
            <Button
              size="small"
              component={RouterLink}
              to={ACCOUNTS_ROUTE}
              sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
            >
              Review on the Accounts page
            </Button>
          </Alert>
        </motion.div>
      )}

      {/* The distinction that IS the product, stated where both pages are reached
          from. A reader who takes these for two views of one dataset misreads
          both. */}
      <motion.div variants={sectionVariants}>
        <Alert severity="info" sx={{ mb: 3, fontFamily: FONT_BODY }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            This is what IPAM <b>discovered</b>, not what it handed out.
          </AlertTitle>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
            Every CIDR below belongs to a resource that exists in an enrolled account, whether or
            not IPAM gave it that address.{' '}
            <RouterLink to={ALLOCATIONS_ROUTE} style={{ color: CK.blue }}>
              Allocations
            </RouterLink>{' '}
            lists only what IPAM handed out of its own pools. A VPC created outside a pool appears
            here and not there — and that difference is the compliance story: it is address space
            nothing is planning around. The <b>Managed by pool</b> column is where the two meet.
          </Typography>
        </Alert>
      </motion.div>

      {/* The CONTEXT read's error. It survives a successful inventory page, which
          is the whole point of splitting the two: a config Prism could not read
          stays reported for as long as it stays unread. */}
      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => { loadContext(); load(''); }} sx={{ mb: 3 }} />
        </motion.div>
      )}

        {/* THE SCOPE-VIEW LAG NOTICE. The table below is real and complete —
            every discovered CIDR is in it — but its three posture columns are
            blank on the rows AWS has not assessed, and a blank column that
            nobody explains reads as a clean one. Stated above the results for
            the same reason the coverage banner is: the table looks perfectly
            healthy, and that is the problem. */}
        {lagNotice && (
          <motion.div variants={sectionVariants}>
            <Alert severity="info" sx={{ mb: 3, fontFamily: FONT_BODY }}>
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>{lagNotice.title}</AlertTitle>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
                {lagNotice.description}
              </Typography>
            </Alert>
          </motion.div>
        )}

        {/* === Filters === */}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              useFlexGap
              flexWrap="wrap"
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Account</InputLabel>
                <Select value={account} label="Account" onChange={(e) => setAccount(e.target.value)}>
                  <MenuItem value="">All accounts</MenuItem>
                  {accountOptions.map((o) => (
                    <MenuItem key={o} value={o} sx={{ fontFamily: FONT_MONO }}>{o}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Region</InputLabel>
                <Select value={region} label="Region" onChange={(e) => setRegion(e.target.value)}>
                  <MenuItem value="">All regions</MenuItem>
                  {regionOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Resource type</InputLabel>
                <Select
                  value={resourceType}
                  label="Resource type"
                  onChange={(e) => setResourceType(e.target.value)}
                >
                  <MenuItem value="">All types</MenuItem>
                  {typeOptions.map((o) => (
                    <MenuItem key={o} value={o}>{formatStatusLabel(o)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 165 }}>
                <InputLabel>Compliance</InputLabel>
                <Select
                  value={compliance}
                  label="Compliance"
                  onChange={(e) => setCompliance(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {complianceOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o === POSTURE_NOT_APPLICABLE ? 'Not applicable' : formatStatusLabel(o)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 155 }}>
                <InputLabel>Overlap</InputLabel>
                <Select value={overlap} label="Overlap" onChange={(e) => setOverlap(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {overlapOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o === POSTURE_NOT_APPLICABLE ? 'Not applicable' : formatStatusLabel(o)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search CIDR, resource or VPC"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flexGrow: 1, minWidth: 220 }}
              />
            </Stack>
          </Paper>
        </motion.div>

        {/* === The table === */}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalSectionHeader
              icon={<LanOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Discovered CIDRs"
              subtitle="Every CIDR AWS knows about. Compliance, overlap and management state come from AWS's IPAM scope view, on VPC rows only — rows it has not reported yet carry real utilization and no posture."
              color={IPAM_COLOR}
              action={(
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    label={`${filtered.length} ${plural(filtered.length, 'CIDR', 'CIDRs')}`}
                    sx={statusChipSx('info')}
                  />
                  <Tooltip title="Overlap is assessed on VPC rows only, and only on the VPCs AWS has reported in the IPAM scope view. A VPC the scope view has not reached is not counted here — nothing has looked at it, so nothing can call it non-overlapping.">
                    <Chip
                      size="small"
                      label={`${overlappingCount} overlapping of ${assessedVpcCount} ${plural(assessedVpcCount, 'VPC', 'VPCs')} assessed`}
                      sx={statusChipSx(
                        // eslint-disable-next-line no-nested-ternary
                        overlappingCount > 0 ? 'error' : (assessedVpcCount > 0 ? 'success' : 'neutral'),
                      )}
                    />
                  </Tooltip>
                  {unassessedVpcCount > 0 && (
                    <Tooltip title="AWS has not reported these VPCs in the IPAM scope view yet, so nothing has assessed them for overlap or compliance. They are counted here rather than folded into the figure beside them, which would read as an all-clear nobody has earned.">
                      <Chip
                        size="small"
                        label={`${unassessedVpcCount} ${plural(unassessedVpcCount, 'VPC', 'VPCs')} not yet assessed`}
                        sx={statusChipSx('warning')}
                      />
                    </Tooltip>
                  )}
                </Stack>
              )}
            />

            {/* The FEED's own error, beside the feed. It never touches `error`
                above, so a failing inventory page cannot clear a failing config
                read and cannot borrow its retry either. */}
            {tableError && (
              <PortalErrorState error={tableError} onRetry={() => load('')} sx={{ mb: 2 }} />
            )}

            {loading && rows.length === 0 ? (
              <PortalLoading label="Loading discovered address space…" color={IPAM_COLOR} />
            ) : coldStart && rows.length === 0 ? (
              <IpamDiscoveryColdStart
                associations={associations}
                enabledAt={config?.costAcknowledgedAt || config?.createdAt}
                // lastDiscoveryRefreshAt is the SERVER's sweep time and belongs
                // in lastSweepAt, not in lastCheckedAt: passing it as "last
                // checked" made a tenant Prism had never read look freshly read.
                lastSweepAt={config?.lastDiscoveryRefreshAt}
                onCheck={checkNow}
                checking={checking}
              />
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1240 }}>
                  <TableHead>
                    <TableRow sx={tableHeadRowSx}>
                      <TableCell sx={headCellSx}>CIDR</TableCell>
                      <TableCell sx={headCellSx}>Resource</TableCell>
                      <TableCell sx={headCellSx}>Account</TableCell>
                      <TableCell sx={headCellSx}>Region</TableCell>
                      <TableCell sx={headCellSx}>VPC</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 130 }}>
                        <Tooltip title="AWS reports IP usage as a fraction of the space in use. It does not compute it for IPv6 space at all, so an IPv6 prefix shows a dash rather than 0 %.">
                          <Box component="span" sx={{ cursor: 'help' }}>Utilization</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={headCellSx}>Compliance</TableCell>
                      <TableCell sx={headCellSx}>Overlap</TableCell>
                      <TableCell sx={headCellSx}>Management</TableCell>
                      <TableCell sx={headCellSx}>Managed by pool</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((r, index) => (
                      <TableRow key={r.id || `${r.ipamScopeId}/${r.resourceId}/${r.resourceCidr}`} hover sx={rowSx(index)}>
                        <TableCell sx={cidrCellSx}>
                          {/* The CIDR cell is the drawer's only entry point: address
                              history is a per-CIDR lookup with no bulk feed, so it has
                              to be opened from a CIDR that is already on screen. */}
                          {/* The history lookup is per (CIDR, IPAM scope) and a
                              discovered-only row has no scope id to look it up in
                              — the drawer says so honestly, but the tooltip must
                              not promise what it cannot deliver. */}
                          <Tooltip title={r.ipamScopeId
                            ? 'View this CIDR’s address history (a live read from AWS)'
                            : 'Address history needs the IPAM scope this CIDR sits in, and AWS has not reported this one in the scope view yet. Available once it does.'}>
                            <Box
                              component="button"
                              type="button"
                              onClick={() => openHistory(r)}
                              sx={{
                                font: 'inherit',
                                color: CK.navy,
                                background: 'none',
                                border: 'none',
                                p: 0,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted',
                                textUnderlineOffset: '3px',
                              }}
                            >
                              {r.resourceCidr || '—'}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Box sx={{ fontFamily: FONT_MONO, fontSize: '0.8125rem', wordBreak: 'break-all' }}>
                            {r.resourceId || '—'}
                          </Box>
                          <Box sx={{ color: CK.muted, fontSize: '0.75rem' }}>
                            {[formatStatusLabel(r.resourceType), r.resourceName]
                              .filter(Boolean).join(' · ') || '—'}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontFamily: FONT_MONO, whiteSpace: 'nowrap' }}>
                          {r.resourceOwnerId || '—'}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                          {r.resourceRegion || '—'}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontFamily: FONT_MONO, wordBreak: 'break-all' }}>
                          {r.vpcId || '—'}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <UtilizationBar
                            value={usagePercent(r.ipUsage)}
                            thresholds={ADDRESS_SPACE_THRESHOLDS}
                            width={110}
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <PostureCell
                            value={r.complianceStatus}
                            meta={COMPLIANCE_META}
                            resourceType={r.resourceType}
                            scopeReported={r.scopeReported === true}
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <PostureCell
                            value={r.overlapStatus}
                            meta={OVERLAP_META}
                            resourceType={r.resourceType}
                            scopeReported={r.scopeReported === true}
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <PostureCell
                            value={r.managementState}
                            meta={MANAGEMENT_META}
                            resourceType={r.resourceType}
                            scopeReported={r.scopeReported === true}
                          />
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontFamily: FONT_MONO, wordBreak: 'break-all' }}>
                          {/* ipam_pool_id IS A SCOPE-VIEW COLUMN, so this cell has the
                              same three states the posture cells do. "not from a
                              pool" is a finding — it is the whole compliance story
                              of this page — and asserting it about a row AWS has
                              not reported in the scope view would manufacture the
                              finding out of a blank. */}
                          {/* eslint-disable-next-line no-nested-ternary */}
                          {r.ipamPoolId || (r.scopeReported !== true ? (
                            <Tooltip title="AWS has not reported this CIDR in the IPAM scope view yet, and pool membership is only visible there. Prism cannot yet say whether a pool governs this prefix — this is unknown, not a finding.">
                              <Box component="span" sx={{ fontFamily: FONT_BODY, color: CK.muted, cursor: 'help' }}>
                                &mdash;
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tooltip title="This CIDR was not allocated from an IPAM pool, so it does not appear on the Allocations page. That gap is the point of this column.">
                              <Box component="span" sx={{ fontFamily: FONT_BODY, color: CK.muted, cursor: 'help' }}>
                                not from a pool
                              </Box>
                            </Tooltip>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* A FAILED READ IS NOT A ZERO. The scoped empty state below
                        claims "AWS reported no CIDRs in these accounts", and that
                        is a claim Prism cannot make when the feed did not answer —
                        including when it answered in an unrecognised shape. That
                        confident zero is exactly what the old tolerant reader used
                        to manufacture, so the error card above stands alone here. */}
                    {!loading && tableError && filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
                            This table is empty because the read failed, not because nothing was
                            found. See the error above.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {!loading && !tableError && filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                          {filtersActive ? (
                            <PortalEmptyState
                              icon={<LanOutlinedIcon sx={{ fontSize: 28 }} />}
                              title="No CIDRs match these filters"
                              description={
                                nextCursor
                                  ? 'Filters apply to the rows loaded so far. Clear a filter, or load more rows and search again.'
                                  : 'Clear a filter to widen the search.'
                              }
                              color={IPAM_COLOR}
                              py={2}
                            />
                          ) : (
                            // A SCOPED zero, never the bare words "no resources
                            // found", AND NEVER A CLAIM ABOUT AWS PRISM HAS NOT
                            // ESTABLISHED. Naming the accounts and Regions that
                            // were actually looked in is what makes an honest zero
                            // distinguishable from a broken one — but it is not
                            // enough on its own: the sentence was live-observed
                            // naming 2 accounts and 1 region over 14 CIDRs AWS had
                            // reported. ipamAddressSpaceZeroState decides which of
                            // the four states this is.
                            <PortalEmptyState
                              icon={<LanOutlinedIcon sx={{ fontSize: 28 }} />}
                              title={zeroState.title}
                              description={zeroState.description}
                              color={IPAM_COLOR}
                              py={2}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {(nextCursor || filtersActive) && !coldStart && (
              <Box
                sx={{
                  mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 2, flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
                  {nextCursor
                    ? `Showing ${filtered.length} of ${rows.length} rows loaded. More pages remain — filters apply only to what has been loaded.`
                    : `Showing ${filtered.length} of ${rows.length} rows. Everything has been loaded.`}
                </Typography>
                {nextCursor && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => load(nextCursor)}
                    disabled={loadingMore}
                    sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </motion.div>

        <motion.div variants={sectionVariants}>
          <Typography
            sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted, mt: 2 }}
          >
            <HistoryIcon sx={{ fontSize: 14, verticalAlign: '-2px', mr: 0.5 }} />
            Click any CIDR to see which resources have held it. That lookup goes straight to AWS
            and is not cached — AWS destroys the history irreversibly when the IPAM is deleted.
          </Typography>
        </motion.div>

      <IpamHistoryDrawer
        open={!!historyRow}
        onClose={() => setHistoryRow(null)}
        cidr={historyRow?.resourceCidr}
        scopeId={historyRow?.ipamScopeId}
      />
    </motion.div>
  );
}
